/**
 * ChartRenderer - Renders MUI X Charts from agent actions
 * Supports single-series (data: [{label, value}]) and multi-series (categories + series) formats.
 */
import { Box, Typography, Paper } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { PieChart } from '@mui/x-charts/PieChart';
import { LineChart } from '@mui/x-charts/LineChart';
import type { ChatAction } from '@diggens/types';

interface ChartRendererProps {
  action: ChatAction;
}

export function ChartRenderer({ action }: ChartRendererProps) {
  const { chart_type, title } = action;
  const data = action.data as Array<{ label: string; value: number }> | undefined;
  const categories = action.categories;
  const series = action.series;
  const isMultiSeries = categories && series && series.length > 0;

  if (!isMultiSeries && (!data || data.length === 0)) {
    return (
      <Paper sx={{ p: 2, my: 1 }}>
        <Typography color="text.secondary">No data available</Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 2, my: 1 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>{title}</Typography>
      <Box sx={{ width: '100%', height: 350 }}>
        {isMultiSeries ? (
          <>
            {chart_type === 'bar' && (
              <BarChart
                xAxis={[{ scaleType: 'band', data: categories }]}
                series={series.map((s) => ({ data: s.data, label: s.name }))}
                height={330}
              />
            )}
            {chart_type === 'line' && (
              <LineChart
                xAxis={[{ scaleType: 'point', data: categories }]}
                series={series.map((s) => ({ data: s.data, label: s.name }))}
                height={330}
              />
            )}
            {chart_type === 'pie' && data && (
              <PieChart
                series={[{ data: data.map((d, i) => ({ id: i, value: d.value, label: d.label })) }]}
                height={330}
              />
            )}
          </>
        ) : (
          <>
            {chart_type === 'bar' && data && (
              <BarChart
                xAxis={[{ scaleType: 'band', data: data.map((d) => d.label) }]}
                series={[{ data: data.map((d) => d.value) }]}
                height={330}
              />
            )}
            {chart_type === 'pie' && data && (
              <PieChart
                series={[{ data: data.map((d, i) => ({ id: i, value: d.value, label: d.label })) }]}
                height={330}
              />
            )}
            {chart_type === 'line' && data && (
              <LineChart
                xAxis={[{ scaleType: 'point', data: data.map((d) => d.label) }]}
                series={[{ data: data.map((d) => d.value) }]}
                height={330}
              />
            )}
          </>
        )}
      </Box>
    </Paper>
  );
}
